/**
 * @file SplitNotesModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-06
 * @modified 2026-10-06
 * @description Add or change the reason an order was split, after the split.
 *              The note belongs to the split, so it is saved on every child of
 *              the order's family (customer_orders.split_notes); the old
 *              wording stays in each order's history (SPLIT_NOTES_EDITED).
 */

import React, { useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Textarea from './Textarea';
import { useSlideOutButton } from './SlideOutButton';
import './OrderHub.css';

function SplitNotesBody({ notes, setNotes, error, saving }) {
  useSlideOutButton([saving]);
  return (
    <div className="split-notes-body">
      <p className="split-notes-intro">
        Why the order was split. It shows on the order and on each split order made from it.
      </p>
      <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} maxLength={2000}
        placeholder="e.g. rep's customer ran out — part of the load went to their neighbour" />
      {error && <p className="divert-error">{error}</p>}
    </div>
  );
}

/**
 * @param {number}   orderId      Any order of the split family.
 * @param {string}   orderNumber
 * @param {string}   notes        The note as it stands.
 * @param {function} onClose
 * @param {function} onSaved      After a save — refresh what is behind.
 */
function SplitNotesModal({ orderId, orderNumber, notes: initial, onClose, onSaved }) {
  const [notes, setNotes] = useState(initial || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/orders/customer-orders/${orderId}/split-notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes: notes.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || `Could not save (${res.status})`);
      onSaved && onSaved(data.split_notes);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Split notes — ${orderNumber || 'order'}`}
      size="md"
      className="split-notes"
      footer={(
        <div className="buttons slideout">
          <div>
            <button data-read-safe className="btn-slideout" onClick={onClose} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button data-write-action className="btn-slideout" onClick={save} disabled={saving}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">save</span></div>
              <span className="btn-label">{saving ? 'Saving...' : 'Save'}</span>
            </button>
          </div>
        </div>
      )}
    >
      <SplitNotesBody notes={notes} setNotes={setNotes} error={error} saving={saving} />
    </Modal>
  );
}

export default SplitNotesModal;
